/**
 * VoiceModePanel — public name for the voice channel panel, symmetric
 * with the future `ChatModePanel` (PR 7).
 *
 * PR 5 of the Multi-Channel Assistant rollout. This component is a thin
 * wrapper around `VoiceBotPanel`, the existing implementation that has
 * been incrementally extracted in waves 1–6d. The wrapper exists so that:
 *
 *   1. Consumers (`AssistantShell`, future `MultiChannelAssistantShell`)
 *      can render `<VoiceModePanel/>` and `<ChatModePanel/>` symmetrically.
 *   2. The internal `VoiceBotPanel` name remains stable for the existing
 *      `widget.js` embed entry, the Playwright tests, and the historical
 *      "extracted from VoiceBotPanel.tsx" comments in helper modules.
 *
 * No behavior change: this is a structural rename only.
 *
 * Codex hard rule #9: recording / AudioContext code lives ONLY here and
 * inside `useVoiceSession`. Chat sessions must never touch this tree.
 */

import type React from 'react';
import { VoiceBotPanel } from './VoiceBotPanel';
import type { VoiceOption, LanguageOption, VoiceHardeningConfig } from '../types';
import type { CrossChannelMemory } from '../hooks/useCrossChannelMemory';

export interface VoiceModePanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Multi-channel only: parent passes a callback to switch to chat;
   *  the voice panel renders a chat-icon button in its header. */
  onSwitchToChat?: () => void;
  switchToChatLabel?: string;
  /**
   * Shared cross-channel conversation memory. When provided AND non-empty
   * at session start, the voice transport injects the prior messages via
   * Realtime `conversation.item.create` events before triggering the
   * model's first response, so the bot continues mid-conversation
   * transparently. Every assistant + user voice turn is also pushed back
   * into this memory so a later switch to chat retains the full history.
   */
  crossChannelMemory?: CrossChannelMemory;
  config: {
    publicKey?: string;
    botName: string;
    companyName: string;
    greeting: string;
    voice: VoiceOption;
    language: LanguageOption;
    temperature: number;
    primaryColor: string;
    panelWidth: number;
    systemPrompt: string;
    calendlyOwnerName: string;
    propertyCode?: string;
    forceVoiceOverride?: boolean;
    recordingsFolderId?: string;
    transcriptsFolderId?: string;
    poweredByText?: string;
    contactFormTiming?: 'none' | 'before_conversation' | 'during_conversation' | 'before_scheduling';
    voiceHardening?: VoiceHardeningConfig;
    blockInterruptionDefault?: boolean;
  };
}

export const VoiceModePanel: React.FC<VoiceModePanelProps> = (props) => {
  return <VoiceBotPanel {...props} />;
};
